import {
  DestroyRef,
  inject,
  SimpleChanges,
  ChangeDetectorRef,
  Component,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';

import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';

import type { UserIdMap } from '@core/account-data';
import { UserSelectors } from '@core/account-data';
import { provideAnalyticsViewContext } from '@core/analytics-legacy';
import { AnalyticsViewContext } from '@core/analytics-types';
import {
  BulkActionsSelectionMessageComponent,
  BulkActionsSelectionTracker,
} from '@core/bulk-actions-legacy';
import {
  collapse,
  filterBoolean,
  NgTemplateAssertType,
  trackByIndex,
  trackByProp,
} from '@core/common-legacy';
import { JobsIdMap, JobsWrapperDataService } from '@core/offers-data';
import { RequisitionsListParam } from '@core/requisitions-types';
import type { Requisition, RequisitionsParams } from '@core/requisitions-types';
import type {
  DataTableColumnGeometryChange,
  DataTableRow,
  IntersectionObserverTemplateContext,
} from '@core/user-interface-legacy';
import {
  DataTableComponent,
  IntersectionObserverEntryComponent,
  PlaceloaderComponent,
} from '@core/user-interface-legacy';
import { TableListGroupHeadingComponent } from '@core/views-ui';

import { RequisitionsListTableComponent } from '../../../components/list/requisitions-list-table/requisitions-list-table.component';
import { RequisitionsListFacade } from '../../../facades/list/requisitions-list.facade';
import type {
  RequisitionsGroupToDisplay,
  RequisitionsTableColumn,
} from '../../../requisitions.types';
import { RequisitionsBulkActionsController } from '../../../services/requisitions-bulk-actions.controller';
import { RequisitionsListTableService } from '../../../services/requisitions-list-table.service';
import { RequisitionsListViewStateService } from '../../../services/requisitions-list-view-state.service';
import { RequisitionsSharedActionsService } from '../../../services/requisitions-shared-actions.service';
import { RequisitionsListEmptyStateContainerComponent } from '../requisitions-list-empty-state-container/requisitions-list-empty-state-container.component';

@Component({
  selector: 'rt-requisitions-list-table-view-container',
  templateUrl: './requisitions-list-table-view-container.component.html',
  styleUrls: ['./requisitions-list-table-view-container.component.less'],
  animations: [collapse],
  imports: [
    RouterModule,
    RequisitionsListEmptyStateContainerComponent,
    RequisitionsListTableComponent,
    IntersectionObserverEntryComponent,
    DataTableComponent,
    PlaceloaderComponent,
    BulkActionsSelectionMessageComponent,
    NgTemplateAssertType,
    TableListGroupHeadingComponent,
  ],
  providers: [provideAnalyticsViewContext(AnalyticsViewContext.Requisitions)],
})
export class RequisitionsListTableViewContainerComponent {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly requisitionsListFacade = inject(RequisitionsListFacade);
  private readonly requisitionsSharedActionsService = inject(RequisitionsSharedActionsService);
  private readonly requisitionsListTableService = inject(RequisitionsListTableService);
  private readonly requisitionsViewState = inject(RequisitionsListViewStateService);
  private readonly bulkActionsController = inject(RequisitionsBulkActionsController);
  private readonly bulkActionsSelectionTracker = inject(BulkActionsSelectionTracker);
  private readonly jobsWrapperDataService = inject(JobsWrapperDataService);
  private readonly cdRef = inject(ChangeDetectorRef);

  private readonly destroyRef = inject(DestroyRef);
  public readonly IntersectionObserverTemplateContext: IntersectionObserverTemplateContext<RequisitionsGroupToDisplay>;

  public readonly requisitionsGroups = input<RequisitionsGroupToDisplay[]>([]);
  public readonly isLoading = input<boolean>(undefined!);
  public readonly params = input<RequisitionsParams>(undefined!);
  public readonly collapsedGroups = input<string[]>(undefined!);
  public readonly showGroups = input<boolean>(undefined!);

  public placeloaderRows: DataTableRow[] = Array(5)
    .fill(0)
    .map((x, i) => ({ id: i }));
  public trackById = trackByProp<{ id: number }>({ prop: 'id' });
  public trackByIndex = trackByIndex;
  public jobsMap: JobsIdMap;
  public usersMap: UserIdMap;
  public columns: RequisitionsTableColumn[];
  public autoFit: boolean;
  public canEditRequisitions: boolean;
  public canCreateJobs: boolean;
  public showRequisitions: boolean;
  public selectedIdsByGroup: Map<string | null, number[]> = new Map();
  public allSelectedIds: Record<number, boolean> = {};

  public ngOnChanges(changes: SimpleChanges): void {
    const params = this.params();
    if (
      changes.params?.currentValue &&
      params[RequisitionsListParam.SortBy] &&
      params[RequisitionsListParam.SortOrder]
    ) {
      this.requisitionsListTableService.toggleColumnSort(
        params[RequisitionsListParam.SortBy],
        params[RequisitionsListParam.SortOrder],
      );
    }

    if (changes.showGroups || changes.requisitionsGroups) {
      this.bulkActionsSelectionTracker.items = this.requisitionsGroups().reduce(
        (acc, req) => [...acc, ...req.requisitions],
        [],
      );

      this.handleRequisitionsVisibility();
    }
  }

  public ngOnInit(): void {
    combineLatest([
      this.jobsWrapperDataService.getJobItemsIdMap$(),
      this.store.select(UserSelectors.idMap),
    ])
      .pipe(filterBoolean, takeUntilDestroyed(this.destroyRef))
      .subscribe(([jobsMap, usersMap]) => {
        this.jobsMap = jobsMap;
        this.usersMap = usersMap;
        this.cdRef.detectChanges();
      });
    this.listenToTableConfigChanges();
    this.listenToBulkActionsSelectChanges();

    this.canEditRequisitions = this.requisitionsListFacade.canEditRequisitions;
    this.canCreateJobs = this.requisitionsListFacade.canCreateJobs;
  }

  public follow(id: number): void {
    this.requisitionsSharedActionsService.follow(id);
  }

  public unfollow(id: number): void {
    this.requisitionsSharedActionsService.unfollow(id);
  }

  public createJob(id: number): void {
    this.requisitionsSharedActionsService.createJob(id);
  }

  public duplicate(id: number): void {
    this.requisitionsSharedActionsService.duplicate(id);
  }

  public delete(requisition: Requisition): void {
    this.requisitionsSharedActionsService.delete(requisition);
  }

  public archive(requisition: Requisition): void {
    this.requisitionsSharedActionsService.archive(requisition);
  }

  public retrieve(requisition: Requisition): void {
    this.requisitionsSharedActionsService.retrieve(requisition);
  }

  public cancelRequest(id: number): void {
    this.requisitionsSharedActionsService.cancelApprovalRequest(id);
  }

  public openRequisition(id: number): void {
    this.router.navigate(['requisitions', id]);
  }

  public handleGeometryChange(geometry: DataTableColumnGeometryChange) {
    this.requisitionsListTableService.handleGeometryChange(geometry);
  }

  public changeGroupVisibility(groupName: string): void {
    this.requisitionsViewState.setGroupVisibility(groupName);
  }

  public handleSortChange(column: RequisitionsTableColumn): void {
    this.requisitionsViewState.setSorting(column);
  }

  public onRowSelect(event: { row: DataTableRow; selected: boolean }): void {
    this.bulkActionsController.selectItem(<any>event.row, event.selected);
  }

  public onGroupSelectedChange(group: RequisitionsGroupToDisplay, isSelected: boolean): void {
    this.bulkActionsController.selectItems(group.requisitions, isSelected);
  }

  public handleRequisitionsVisibility() {
    if (this.showGroups()) {
      this.showRequisitions = !!this.requisitionsGroups().length;
    } else {
      const requisitions = this.requisitionsGroups()[0]?.requisitions;
      this.showRequisitions = requisitions ? !!requisitions.length : true;
    }
  }

  private listenToTableConfigChanges(): void {
    this.requisitionsListTableService.tableConfig$
      .pipe(filterBoolean, takeUntilDestroyed(this.destroyRef))
      .subscribe(config => {
        this.columns = config.visibleColumns;
        this.autoFit = config.autoFit;
        this.cdRef.detectChanges();
      });
  }

  private listenToBulkActionsSelectChanges(): void {
    this.bulkActionsController.selectedIdsDict$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(dict => {
        this.allSelectedIds = dict;
        this.selectedIdsByGroup = new Map(
          this.requisitionsGroups().map(group => [
            group.name!,
            group.requisitions.reduce(
              (acc, req) => [...acc, ...(!!dict[req.id] ? [req.id] : [])],
              [],
            ),
          ]),
        );
        this.cdRef.detectChanges();
      });
  }
}
